/**
 * Read-only location preview — a non-editable map dialog that lets staff verify a
 * service location without leaving the order/project flow or dropping into the
 * editing picker, e.g. when picking up an order someone else created.
 *
 * Two trigger modes:
 *  - Uncontrolled (default): renders its own subtle map-icon button.
 *  - Controlled (pass `open` + `onOpenChange`): renders NO trigger; the parent
 *    supplies one — e.g. the whole location card is the clickable trigger.
 *
 * The dialog map goes through the active provider connector (`getMapConnector()`),
 * so it tracks `VITE_MAPS_PROVIDER` like every other map. No pin drop, no commit;
 * the dialog is purely for looking (streets/satellite toggle for inspection).
 */
import React, { useState } from 'react';
import {
  Button,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Map20Regular } from '@fluentui/react-icons';
import { BaseDialog } from '../BaseDialog';
import { getMapConnector } from '../../../../shared/maps';

// Resolved once at module load — provider doesn't switch at runtime (mirrors
// LocationPicker / OperationsMap).
const { MapView, Marker } = getMapConnector();

// Pin color matches the editable pin in LocationPicker for visual continuity.
const PIN_COLOR = '#f97316';

const useStyles = makeStyles({
  dialogMap: {
    width: '100%',
    height: '380px',
    ...shorthands.borderRadius('4px'),
    ...shorthands.overflow('hidden'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  dialogAddress: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    marginBottom: '8px',
  },
  styleToggle: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    zIndex: 6,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius('4px'),
  },
});

export interface LocationPreviewProps {
  lat: number;
  lng: number;
  /** Used as the dialog title; falls back to the address, then coordinates. */
  name?: string;
  /** Shown under the map in the dialog and as the title fallback. */
  address?: string;
  /** When provided, the component is controlled and renders NO trigger button;
   *  the parent owns the trigger (e.g. a clickable card). Pair with onOpenChange. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const LocationPreview: React.FC<LocationPreviewProps> = ({
  lat,
  lng,
  name,
  address,
  open: openProp,
  onOpenChange,
}) => {
  const styles = useStyles();
  const controlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = controlled ? openProp : openState;
  const setOpen = (next: boolean) => {
    if (!controlled) setOpenState(next);
    onOpenChange?.(next);
  };
  const [satellite, setSatellite] = useState(false);

  const center = { lat, lng };
  const coords = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  const title = name?.trim() || address?.trim() || coords;

  return (
    <>
      {!controlled && (
        <Tooltip content="Preview location" relationship="label">
          <Button
            appearance="subtle"
            icon={<Map20Regular />}
            onClick={() => setOpen(true)}
            aria-label="Preview location on map"
            style={{ minWidth: 'auto' }}
          />
        </Tooltip>
      )}

      <BaseDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        title={title}
        maxWidth="760px"
        showCloseButton
        actions={
          <Button appearance="primary" onClick={() => setOpen(false)}>
            Close
          </Button>
        }
      >
        {address?.trim() && <div className={styles.dialogAddress}>{address}</div>}
        <div className={styles.dialogMap} style={{ position: 'relative' }}>
          <MapView
            key={`${coords}-${satellite ? 'sat' : 'str'}`}
            center={center}
            zoom={16}
            style={satellite ? 'satellite' : 'streets'}
            style_={{ width: '100%', height: '100%' }}
          >
            <Marker position={center} color={PIN_COLOR} />
          </MapView>
          <Button
            className={styles.styleToggle}
            size="small"
            appearance="secondary"
            onClick={() => setSatellite((s) => !s)}
          >
            {satellite ? 'Streets' : 'Satellite'}
          </Button>
        </div>
      </BaseDialog>
    </>
  );
};

export default LocationPreview;
